import React from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { analytics, useCurrentAgreement, useHost, useHostRefetch } from '@economic/react-app';
import { setLocale } from 'yup';
import { setupHostEndpoints } from '~/app/shared/util/api/setupHostEndpoints';
import { t, useLocalization } from '../shared/components/l10n/l10n';
import routeTextsMap from '../../utilities/routeTextsMap';

declare global {
    interface Window {
        gotoRoute: NavigateFunction;
        refreshRouting: () => void;
        gtmEvent: (event: string, data: object) => void;
    }
}

export const useEconomic = () => {
    const navigate = useNavigate();
    const agreement = useCurrentAgreement();
    const host = useHost();
    const refetch = useHostRefetch();

    // fetch global, preloaded texts from Startup component... ideally we replace this with new translations but that is a huge task
    const loading = useLocalization(globalTextIds);

    React.useEffect(() => {
        setupHostEndpoints(host.services);

        if (window.top) {
            window.top.document.title = `${agreement.number} - ${agreement.name}`;

            // provides an integration for legacy e-conomic to trigger route changes
            window.top.gotoRoute = (to, state = {}) => {
                if (to.startsWith('#')) {
                    navigate(window.location.pathname + to, { state });
                } else {
                    navigate(to, { state });
                }
            };

            window.top.refreshRouting = async () => {
                refetch();
                return Promise.resolve();
            };

            // provides an integration for legacy e-conomic to trigger google tag manager events
            window.top.gtmEvent = analytics.gtm;
        }
    }, []);

    React.useEffect(() => {
        // Place any logic to run at startup here

        if (!loading) {
            setYupLocale();
        }
    }, [loading]);

    return loading;
};

export function setYupLocale(): void {
    setLocale({
        mixed: {
            required: t`FORM_FIELD_LABEL_REQUIRED`,
            notType: t`FORM_FIELD_ERROR_TYPE`,
        },
    });
}

export const globalTextIds = [
    ...Object.values(routeTextsMap),
    'LEFTMENU_FIREBIRD_QUICKLINKS',
    'TOP_NAVIGATION_MORE_TEXT',
    'QUICK_SEARCH_NO_RESULTS',
    'QUICK_SEARCH_PLACEHOLDER',
    'AGREEMENT_SELECTOR_SEARCH_PLACEHOLDER',
    'AGREEMENT_SELECTOR_ADD_ACCOUNT',
    'AGREEMENT_SELECTOR_LOG_OUT',
    'AGREEMENT_SELECTOR_ADMINISTRATOR',
    'AGREEMENT_SELECTOR_DEVELOPER',
    'CLIENT_NOTIFICATION_INTRODUCTION_TITLE',
    'CLIENT_NOTIFICATION_INTRODUCTION_DESCRIPTION',
    'CLIENT_NOTIFICATION_INTRODUCTION_INFO',
    'AGREEMENT_SELECTOR_ADMINISTRATOR',
    'AGREEMENT_SELECTOR_DEVELOPER',
    'CHOICE_LANGUAGELAYER_NONE',
    'BUTTON_OK',
    'SHARED_FORM_OK',
    'BUTTON_SAVE',
    'BUTTON_SAVE_AND_NEW',
    'FORM_BUTTONS_SAVE_AND_SELECT',
    'BUTTON_CANCEL',
    'BUTTON_CLOSE',
    'BUTTON_CREATE_ENTRY',
    'BUTTON_NEXT',
    'BUTTON_BACK',
    'BUTTON_PRINT',
    'BUTTON_IMPORT',
    'BUTTON_EXPORT_TO_EXCEL',
    'BUTTON_DELETE',
    'BUTTON_BAR',
    'BUTTON_EDIT',
    'BUTTON_COPY',
    'BUTTON_DELETE_SHORTCUT',
    'BUTTON_EDIT_SHORTCUT',
    'BUTTON_COPY_SHORTCUT',
    'BUTTON_SEND',
    'FORM_LEDGER_EDITFIELD_DISTRIBUTION',
    'TOAST_LOADING',
    'TOAST_UNEXPECTED_ERROR',
    'TOAST_UNEXPECTED_ERROR_WITH_ID',
    'TOAST_COPIED_TO_CLIPBOARD',
    'FORM_FIELD_LABEL_ACCOUNT',
    'FORM_FIELD_LABEL_ACCOUNT_NUMBER',
    'FORM_FIELD_LABEL_ACCOUNTING_YEAR',
    'FORM_FIELD_LABEL_ACCOUNT_TYPE',
    'FORM_FIELD_LABEL_NUMBER',
    'FORM_FIELD_LABEL_NAME',
    'FORM_FIELD_LABEL_USER',
    'FORM_FIELD_LABEL_CONTRA_ACCOUNT_NUMBER',
    'FORM_FIELD_LABEL_VAT_ACCOUNT_CODE',
    'FORM_FIELD_LABEL_VAT_CONTRA_ACCOUNT_CODE',
    'FORM_FIELD_LABEL_TOTAL_FROM_ACCOUNT',
    'FORM_FIELD_LABEL_OPENING_ACCOUNT',
    'FORM_FIELD_LABEL_REALISATION_ACCOUNT',
    'FORM_FIELD_LABEL_KEY_FIGURE_CODE',
    'FORM_FIELD_LABEL_AMOUNT',
    'FORM_FIELD_LABEL_VAT_AMOUNT',
    'FORM_FIELD_LABEL_CURRENCY',
    'FORM_FIELD_LABEL_CURRENCY_EXCHANGE_RATE',
    'FORM_FIELD_LABEL_DATE',
    'FORM_FIELD_LABEL_DUE_DATE',
    'FORM_FIELD_LABEL_ENTRY_TYPE',
    'FORM_FIELD_LABEL_INVOICE_NUMBER',
    'FORM_FIELD_LABEL_SUPPLIER_NUMBER',
    'FORM_FIELD_LABEL_CUSTOMER_NUMBER',
    'FORM_FIELD_LABEL_DEPARTMENT_NUMBER',
    'FORM_FIELD_LABEL_ENTRY_NUMBER',
    'FORM_FIELD_LABEL_DEBIT_CREDIT',
    'FORM_FIELD_LABEL_TEXT',
    'FORM_FIELD_LABEL_ACCRUALS',
    'FORM_FIELD_LABEL_ACCRUALS_START_DATE',
    'FORM_FIELD_LABEL_ACCRUALS_END_DATE',
    'FORM_FIELD_LABEL_DIMENSION_UNIT1',
    'FORM_FIELD_LABEL_DIMENSION_UNIT2',
    'FORM_FIELD_LABEL_DIMENSION_QUANTITY1',
    'FORM_FIELD_LABEL_DIMENSION_QUANTITY2',
    'FORM_FIELD_LABEL_PAYMENT_DETAILS',
    'FORM_FIELD_LABEL_PAYMENT_DETAILS_FI_SUPPLIER_NUMBER',
    'FORM_FIELD_LABEL_PAYMENT_DETAILS_OCR_LINE',
    'FORM_FIELD_LABEL_PAYMENT_DETAILS_MESSAGE',
    'FORM_FIELD_LABEL_PAYMENT_DETAILS_GIRO_ACCOUNT',
    'PAYMENTS_PAYMENTTYPES_CREDITORIDNAME_BANKTRANSFER',
    'FORM_FIELD_LABEL_PAYMENT_DETAILS_IBAN_NUMBER',
    'FORM_FIELD_PAYMENT_DETAILS_ADDITIONAL_FIELD_IBAN',
    'FORM_FIELD_LABEL_PAYMENT_DETAILS_OCR_OPTIONAL_MESSAGE',
    'FORM_FIELD_LABEL_PROJECT',
    'FORM_FIELD_LABEL_PROJECT_NUMBER',
    'FORM_FIELD_LABEL_COST_TYPE_NUMBER',
    'FORM_FIELD_LABEL_EMPLOYEE_NUMBER',
    'FORM_FIELD_LABEL_ACCESS',
    'FORM_FIELD_LABEL_BLOCK_DIRECT_ENTRIES',
    'FORM_FIELD_LABEL_TOTAL_INTERVALS',
    'FORM_FIELD_LABEL_GROUP',
    'FORM_FIELD_LABEL_JOBROLE',
    'FORM_FIELD_LABEL_JOURNAL',
    'FORM_FIELD_LABEL_LAYOUT',
    'FORM_FIELD_LABEL_LANGUAGE',
    'FORM_FIELD_LABEL_VAT_ZONE',
    'FORM_FIELD_LABEL_PHONE',
    'FORM_FIELD_LABEL_EMAIL',
    'FORM_FIELD_LABEL_ADDRESS',
    'FORM_FIELD_LABEL_ADDRESS_ZIP_CODE',
    'FORM_FIELD_LABEL_ADDRESS_TOWN',
    'FORM_FIELD_LABEL_ADDRESS_COUNTRY',
    'FORM_FIELD_LABEL_COST_ACCOUNT',
    'FORM_FIELD_LABEL_AUTOMATIC_TEXT',
    'FORM_FIELD_LABEL_CVR',
    'FORM_FIELD_LABEL_PAYMENT_TERMS',
    'FORM_FIELD_LABEL_ATTENTION',
    'FORM_FIELD_LABEL_YOUR_REF',
    'FORM_FIELD_LABEL_OUR_REF',
    'FORM_FIELD_LABEL_TYPE',
    'FORM_FIELD_LABEL_MARKUP_PERCENTAGE',
    'FORM_FIELD_LABEL_BANK_ACCOUNT',
    'FORM_FIELD_LABEL_ACCOUNT_ONGOING',
    'FORM_FIELD_LABEL_ACCOUNT_CLOSED',
    'FORM_FIELD_LABEL_ACCESS_OPEN',
    'FORM_FIELD_LABEL_ACCESS_BARRED',
    'FORM_FIELD_LABEL_DEBIT',
    'FORM_FIELD_LABEL_CREDIT',
    'FORM_FIELD_LABEL_REQUIRED',
    'FORM_FIELD_LABEL_ACCESS_FROM_INVENTORY',
    'FORM_FIELD_LABEL_ACCOUNT_FOR_FREIGHT',
    'FORM_FIELD_LABEL_ACCOUNT_FOR_CHARGE',
    'FORM_FIELD_LABEL_ACCOUNT_FOR_BULK_DISCOUNT',
    'FORM_FIELD_LABEL_CONTRA_ACCOUNT_SUPPLIER_MARKUP',
    'FORM_FIELD_LABEL_CONTRA_ACCOUNT_COST_PRICE_MARKUP',
    'FORM_FIELD_LABEL_ALLOCATE_TO_PRODUCTS',
    'FORM_FIELD_LABEL_SUPPLIER_GROUP',
    'FORM_FIELD_LABEL_AUTOMATIC_NUMBERING',
    'FORM_FIELD_LABEL_PAYMENT_DETAILS_PAYMENT_DATE',
    'FORM_FIELD_LABEL_PAYMENT_DETAILS_CASH_ACCOUNT',
    'FORM_FIELD_LABEL_PERSONAL_MESSAGE',
    'FORM_FIELD_ERROR_MAX_N_CHARACTERS',
    'FORM_FIELD_ERROR_MIN_N_CHARACTERS',
    'FORM_FIELD_ERROR_MAX_DIGITS',
    'FORM_FIELD_ERROR_MAX_CHARACTERS',
    'FORM_FIELD_ERROR_POSITIVE_NUMBER',
    'FORM_FIELD_ERROR_ACCOUNT_NONEXISTING',
    'FORM_FIELD_ERROR_ACCOUNT_BARRED',
    'FORM_VOUCHERNO_ERROR_STARTDATE_ENDDATE',
    'FIREBIRD_FORM_SUPPLIER_GROUP_TAB_STOCK',
    'FIREBIRD_FORM_CUSTOMERS_TAB_GENERAL',
    'FIREBIRD_FORM_SUPPLIERS_TAB_REFERENCESANDPAYMENT',
    'FIREBIRD_FORM_CUSTOMERS_TAB_ADDITIONAL',
    'FIREBIRD_FORM_CUSTOMERS_TAB_DELIVERY',
    'FIREBIRD_FORM_SUPPLIERS_TAB_GENERAL',
    'FIREBIRD_FORM_CUSTOMERS_TAB_PRICING',
    'FIREBIRD_LEFTMENU_INVENTORY_SUPPLIERS_GROUP',
    'MODAL_CURRENCIES_TITLE',
    'MODAL_CHART_OF_ACCOUNTS_TITLE',
    'FIREBIRD_LEFTMENU_SALES_DISCOUNTGROUPS',
    'FIREBIRD_LEFTMENU_INVENTORY_PRODUCTS',
    'LEFTMENU_SETTINGS_DEPARTMENTS',
    'FORM_DELIVERYPLACE_HEADLINE',
    'FIREBIRD_FORM_CUSTOMERS_LISTHEADLINE',
    'FORM_EXPENSES_HEADLINE',
    'FORM_EXPENSEGROUPS_HEADLINE',
    'FORM_CONTACTS_HEADLINE',
    'FORM_EMPLOYEEGROUPS_HEADLINE',
    'FORM_PRICEGROUP_HEADLINE',
    'FORM_CASETASKEMPLOYEES_HEADLINE',
    'FORM_CASES_APPLETHEADLINE',
    'FORM_GOODSGROUPS_HEADLINE',
    'DOCUMENT_INBOX_WORKFLOW_ATTACH_TO_ENTRY_BUTTON',
    'DOCUMENT_INBOX_DOCUMENT_TOOLTIP_PRINT_DOCUMENT',
    'DOCUMENT_INBOX_DOCUMENT_TOOLTIP_DRAG_DOCUMENT',
    'TOAST_DOCUMENT_REQUEST_ATTACH_SUCCESS',
    'TOAST_INBOX_WORKFLOW_ATTACH_ERROR',
    'DOCUMENT_INBOX_TOOLTIP_MOVE_TO_TOP',
    'DOCUMENT_INBOX_TOOLTIP_MOVE_UP',
    'DOCUMENT_INBOX_TOOLTIP_MOVE_DOWN',
    'DOCUMENT_INBOX_TOOLTIP_MOVE_TO_BOTTOM',
    'DOCUMENT_INBOX_TOOLTIP_SMARTSCAN_STATUS_ANALYZING',
    'DOCUMENT_INBOX_TOOLTIP_SMARTSCAN_STATUS_ANALYZED',
    'DOCUMENT_INBOX_TOOLTIP_SMARTSCAN_STATUS_ERROR',
    'FORM_ACCOUNTS_CREATETEXT',
    'FORM_ACCOUNTS_COLBUTTON_ALT_ACCOUNTCARD',
    'FORM_PAYABLES_CREATETEXT',
    'FORM_PAYABLES_CREATETEXT_INLINE',
    'FORM_PAYABLESGROUPS_CREATETEXT',
    'FORM_EMPLOYEES_CREATETEXT',
    'FORM_EMPLOYEEGROUPS_CREATETEXT',
    'FORM_PRICEGROUP_CREATETEXT',
    'FORM_REBATEGROUP_CREATETEXT',
    'FORM_DEPARTMENT_CREATETEXT',
    'FORM_RECEIVABLES_CREATETEXT',
    'FORM_RECEIVABLES_ERROR_CUSTOMERNONOTFOUND',
    'FORM_RECEIVABLESGROUPS_CREATETEXT',
    'FORM_EXPENSES_CREATETEXT',
    'FORM_EXPENSEGROUPS_CREATETEXT',
    'FORM_SUPPLIER_SETUP_EDITTEXT',
    'FORM_SUPPLIER_SETUP_SUB_HEADING',
    'FORM_STOCKREBATEGROUP_HEADLINE',
    'FORM_LOCATION_HEADLINE',
    'FORM_CATEGORY_HEADLINE',
    'FIREBIRD_FORM_COA_TAB_DIMENSION',
    'FORM_FIELD_LABEL_DIMENSION_MANDATORY_DEPARTMENT',
    'FORM_FIELD_LABEL_DIMENSION_MANDATORY_UNIT',
    'MODAL_TITLE_FIND_ENTRY_BY_ACCOUNTING_YEAR',
    'MODAL_FIND_ENTRY_BY_ACCOUNTING_YEAR_FROM_DATE',
    'MODAL_FIND_ENTRY_BY_ACCOUNTING_YEAR_TO_DATE',
    'MODAL_FIND_ENTRY_BY_ACCOUNTING_YEAR_HIDE_ENTRIES_WITH_ATTACHMENT',
    'TABLE_COLUMN_ENTRY_STATUS_BOOKED',
    'TABLE_COLUMN_ENTRY_TYPE',
    'TABLE_COLUMN_ENTRY_NUMBER',
    'TABLE_COLUMN_ENTRY_DATE',
    'TABLE_COLUMN_ENTRY_TEXT',
    'TABLE_COLUMN_ENTRY_AMOUNT',
    'TABLE_COLUMN_ENTRY_ACCOUNT_NUMBER',
    'TABLE_COLUMN_ENTRY_CONTRA_ACCOUNT_NUMBER',
    'TABLE_COLUMN_ENTRY_CURRENCY',
    'TABLE_COLUMN_ENTRY_ATTACHMENT',
    'FORM_FIELD_PAYMENT_DETAILS_OPTION_NONE',
    'FORM_FIELD_PAYMENT_DETAILS_OPTION_BANK_TRANSFER',
    'FORM_FIELD_PAYMENT_DETAILS_OPTION_ENTRY_ONLY',
    'ERROR_CODE_E04000',
    'ERROR_CODE_E06830',
    'ERROR_CODE_E06841',
    'ERROR_CODE_E06850',
    'ERROR_CODE_E06880',
    'ERROR_CODE_E06890',
    'ERROR_CODE_E07020',
    'ERROR_CODE_E07031',
    'ERROR_CODE_E07040',
    'ERROR_CODE_E07070',
    'ERROR_CODE_E07090',
    'ERROR_CODE_E07130',
    'ERROR_CODE_E07140',
    'ERROR_CODE_E07150',
    'ERROR_CODE_E07190',
    'ERROR_CODE_E08002',
    'ERROR_CODE_E08003',
    'ERROR_CODE_E08004',
    'ERROR_CODE_E08005',
    'ERROR_CODE_E04030',
    'ERROR_CODE_E06520',
    'ERROR_CODE_AccountDoesNotExist',
    'ERROR_CODE_AccountIsNotBalanceOrProfitAndLossType',
    'ERROR_CODE_AccountIdChangeNotAllowed',
    'ERROR_CODE_AlreadyExists',
    'ERROR_CODE_DepartmentIsBarred',
    'ERROR_CODE_InvalidFieldValue',
    'ERROR_CODE_FieldValueLengthTooLong',
    'ERROR_CODE_ModuleNotActive',
    'ERROR_CODE_RequiredField',
    'ERROR_CODE_ProductNumberAlreadyExists',
    'ERROR_CODE_ProductNumberChangeSettingNotEnabled',
    'ERROR_CODE_ProductUsedInWebshop',
    'ERROR_CODE_ProductGroupDoesNotExist',
    'ERROR_CODE_ProductGroupWithWithdrawalCanNotBeChangedToGroupWithService',
    'ERROR_CODE_ProductGroupChangeDisallowedByWebShop',
    'ERROR_CODE_ProductGroupAlreadyExists',
    'ERROR_CODE_UsedByCustomerGroup',
    'ERROR_CODE_CannotChangeProductGroupNumber',
    'ERROR_CODE_CannotDisableInventoryWithdrawal',
    'ERROR_CODE_InventoryWithdrawalCannotHaveAccruals',
    'ERROR_CODE_CannotDeleteProductGroupConnectedToProducts',
    'ERROR_CODE_ProductUsedOnInvoiceLines',
    'ERROR_CODE_ProductUsedOnDraftInvoiceLines',
    'ERROR_CODE_ProductUsedInInventoryMovements',
    'ERROR_CODE_ProductUsedInSubscriptions',
    'ERROR_CODE_InvalidAmount',
    'ERROR_CODE_InvalidAmountValue',
    'ERROR_CODE_SMARTBANK_ERROR_ACCOUNT_DOES_NOT_EXIST',
    'ERROR_CODE_SMARTBANK_ERROR_PAYMENT_ACCOUNT_ALREADY_PAIRED',
    'ERROR_CODE_SupplierNumberChangeNotAllowed',
    'ERROR_CODE_COMPANY_GLN_REGISTERED_FOR_NEMHANDEL',
    'ERROR_CODE_CVR_INVALID',
    'ERROR_CODE_InvalidUrl',
    'ERROR_CODE_InvalidName',
    'ERROR_CODE_AlreadyExistingNameOrUrl',
    'ERROR_CODE_InvalidAccountId',
    'ERROR_CODE_InvalidAccountType',
    'ERROR_CODE_InvalidAccountName',
    'ERROR_CODE_AccountIdAlreadyInUse',
    'ERROR_CODE_TotalFromAccountDoesNotExist',
    'ERROR_CODE_TotalIntervalsValueContainsInvalidChars',
    'ERROR_CODE_IntervalNotConsturctedCorrectly',
    'ERROR_CODE_IntervalHasOverlappingValues',
    'ERROR_CODE_BarredAccountUsedByProductGroups',
    'ERROR_CODE_BarredAccountUsedByVatAccounts',
    'FORM_FIELD_ERROR_DATE_NOT_WITHIN_EXISTING_ACCOUNTING_YEAR',
    'FORM_FIELD_ERROR_AT_LEAST_N_CHARACTERS',
    'FORM_FIELD_ERROR_EXACTLY_N_CHARACTERS',
    'FORM_FIELD_ERROR_DUPLICATE_INVOICE_NUMBER',
    'FORM_FIELD_ERROR_TYPE',
    'GUIDED_TOUR_BUTTON_BACK',
    'GUIDED_TOUR_BUTTON_CLOSE',
    'GUIDED_TOUR_BUTTON_DONE',
    'GUIDED_TOUR_BUTTON_NEXT',
    'GUIDED_TOUR_BUTTON_SKIP',
    'FORM_LISTVIEW_FILTER_PLACEHOLDER',
    'INBOX_EXPORT_ENTRIES_FILE_NAME',
    'INBOX_GENERATING_EXPORT_TO_EXCEL',
    'FORM_ASSETS_GROUP_HEADLINE',
    'FORM_ASSETS_GROUP_ADD_NEW',
    'FORM_FIELD_LABEL_ASSETS_GROUP',
    'FORM_FIELD_LABEL_ASSETS_ASSET_NUMBER',
    'FORM_FIELD_LABEL_ASSETS_START_DATE',
    'FORM_FIELD_LABEL_ASSETS_USEFUL_LIFE',
    'FORM_LEDGER_EDITFIELD_ASSETNUMBER_WARNING_ALREADYINUSE',
    'FORM_ASSETS_GROUP_HEADLINE_APPLET',
    'FORM_FIELD_LABEL_USEFUL_LIFE',
    'FORM_FIELD_LABEL_RESIDUAL_VALUE',
    'FORM_FIELD_LABEL_ADDITIONS_ACCOUNT',
    'FORM_FIELD_LABEL_DEPRECIATION_BALANCE_SHEET_ACCOUNT',
    'FORM_FIELD_LABEL_DEPRECIATION_PROFIT_LOSS_ACCOUNT',
    'FORM_FIELD_LABEL_DEPRECIATION_REVERSED_ACCOUNT',
    'FORM_FIELD_LABEL_DISPOSALS_ACCOUNT',
    'FORM_FIELD_LABEL_NET_BOOKED_VALUE_ACCOUNT',
    'FORM_FIELD_LABEL_ADDITIONS_CONTRA_ACCOUNT',
    'FORM_FIELD_LABEL_DEPRECIATION_CONTRA_ACCOUNT',
    'FORM_FIELD_ERROR_ASSETS_AMOUNT_CANNOT_BE_NEGATIVE',
    'LABEL_ELECTRONIC_DOCUMENT',
    'DOCUMENT_MANAGEMENT_DOCUMENT_LIST_PAGE_X_OF_Y',
    'DOCUMENT_MANAGEMENT_DOCUMENT_LIST_X_PAGES',
    'DISTRIBUTION_TEMPLATE_DROPDOWN_OPTION_GROSS',
    'DISTRIBUTION_TEMPLATE_DROPDOWN_OPTION_NET',
    'DISTRIBUTION_TEMPLATE_HEADING',
    'DISTRIBUTION_TEMPLATE_NEW_ROW',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_ACCOUNTNUMBER',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_AMOUNT',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_ACCOUNTNAME',
    'DAYBOOK_NAVIGATION_LIST_DEPARTMENT',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_QUANTITY1',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_QUANTITY2',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_VAT',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_DEBIT_OR_CREDIT',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_GROSS_OR_NET',
    'DISTRIBUTION_TEMPLATE_ROW_COLUMN_HEADING_TEXT',
    'DISTRIBUTION_TEMPLATE_AMOUNT_FOR_DISTRIBUTION',
    'DISTRIBUTION_TEMPLATE_APPLY_TEMPLATE',
    'DISTRIBUTION_TEMPLATE_SAVE_TEMPLATE',
    'DISTRIBUTION_TEMPLATE_NEW_TEMPLATE',
    'DISTRIBUTION_TEMPLATE_REMAINDER',
    'DISTRIBUTION_TEMPLATE_USE_EXISTING_TEMPLATE',
    'DISTRIBUTION_TEMPLATE_SAVE_TITLE',
    'DISTRIBUTION_TEMPLATE_SAVE_TEMPLATENAME',
    'DISTRIBUTION_TEMPLATE_SAVE_AMOUNT',
    'DISTRIBUTION_TEMPLATE_SAVE_TEXT',
    'DISTRIBUTION_TEMPLATE_SAVE_DEPARTMENT',
    'DISTRIBUTION_TEMPLATE_SAVE_QUANTITY1',
    'DISTRIBUTION_TEMPLATE_SAVE_QUANTITY2',
    'DISTRIBUTION_TEMPLATE_VIEW_DOCUMENT',
    'EONBOARDING_CVRVALIDATION_STATUS_MISMATCH_DETAILS',
    'EONBOARDING_CVRVALIDATION_STATUS_INUSE_DETAILS',
    'EONBOARDING_CVRVALIDATION_STATUS_INVALID_DETAILS',
    'EONBOARDING_CVRVALIDATION_CVRSTATUS',
    'EONBOARDING_CVRVALIDATION_COMPANY_NAME',
    'EONBOARDING_CVRVALIDATION_STATUS_INVALID',
    'EONBOARDING_CVRVALIDATION_STATUS_INUSE',
    'EONBOARDING_CVRVALIDATION_STATUS_VALID',
    'EONBOARDING_CVRVALIDATION_STATUS_INVALID_GLN_DETAILS',
    'EONBOARDING_CVRVALIDATION_STATUS_NOT_ELIGIBLE_FOR_FREE_GLN_DETAILS',
    'EONBOARDING_REGISTRATION_ERROR',
    'EONBOARDING_REGISTRATION_ERROR_DUPLICATE_CVR',
    'EONBOARDING_LOOKUP_ERROR',
    'EONBOARDING_TEXT_HEADER',
    'EONBOARDING_TEXT_SUBHEADER',
    'EONBOARDING_TOC_CHECKBOX',
    'EONBOARDING_LEARN_MORE_BUTTON',
    'EONBOARDING_LEARN_MORE_BUTTON_URL',
    'EONBOARDING_ACTIVATE_BUTTON',
    'EONBOARDING_CVR_NUMBER_LABEL',
    'EONBOARDING_SUCCESS_HEADLINE',
    'EONBOARDING_SUCCESS_SUBHEADER',
    'EONBOARDING_SUCCESS_GETSTARTED_BUTTON',
    'EONBOARDING_SUCCESS_CONTACTPOINT_ECOPEDIA',
    'EONBOARDING_SUCCESS_CONTACTPOINT_ECOPEDIA_LINK',
    'EONBOARDING_SUCCESS_CONTACTPOINT_ECOPEDIA_LINK_URL',
    'EONBOARDING_SUCCESS_CONTACTPOINT_SUPPORT',
    'EONBOARDING_SUCCESS_CONTACTPOINT_SUPPORT_PHONENO',
    'EONBOARDING_SPLASH_HEADLINE',
    'EONBOARDING_SPLASH_SUBTITLE',
    'EONBOARDING_SPLASH_SELLINGPOINT_ONE',
    'EONBOARDING_SPLASH_SELLINGPOINT_TWO',
    'EONBOARDING_SPLASH_SELLINGPOINT_THREE',
    'EONBOARDING_SPLASH_LEARN_MORE_BUTTON_TEXT',
    'EONBOARDING_SPLASH_LEARN_MORE_BUTTON_URL',
    'EONBOARDING_SPLASH_CONTINUE_BUTTON_TEXT',
    'LABEL_LANGUAGE_DANISH',
    'LABEL_LANGUAGE_ENGLISH',
    'HANGER_INBOX',
    'HEADER_SETTINGS_BUTTON_HANGER_TITLE',
    'HEADER_SETTINGS_BUTTON_HANGER_DESCRIPTION',
    'HEADER_TAB_LIVECHAT',
    'HEADER_TAB_INBOX',
    'HEADER_TAB_SMARTINBOX',
    'HEADER_TAB_NEW_BADGE',
    'HEADER_TAB_MARKET',
    'HEADER_TAB_INTERCOM',
    'HEADER_TAB_ECOPEDIA',
    'HEADER_TAB_SETTINGS',
    'HEADER_TAB_NOTIFICATIONS',
    'NOTIFICATIONS_PANEL_HEADLINE',
    'NOTIFICATIONS_PANEL_MARK_ALL_READ_BUTTON',
    'NOTIFICATIONS_PANEL_SETTINGS_BUTTON',
    'NOTIFICATIONS_PANEL_SEE_ALL_BUTTON',
    'NOTIFICATIONS_PANEL_EMPTY_HEADLINE',
    'NOTIFICATIONS_PANEL_EMPTY_DESCRIPTION',
    'NOTIFICATIONS_PANEL_EMPTY_MORE_INFO',
    'NOTIFICATIONS_PANEL_EMPTY_MORE_INFO_URL',
    'NOTIFICATIONS_PANEL_NOTIFICATION_MARK_AS_READ',
    'NOTIFICATIONS_PANEL_NOTIFICATION_UNREAD',
    'NOTIFICATIONS_JUST_NOW',
    'NOTIFICATIONS_LAST_HOUR',
    'NOTIFICATIONS_ONE_HOUR_AGO',
    'NOTIFICATIONS_N_HOURS_AGO',
    'NOTIFICATIONS_N_MINUTES_AGO',
    'NOTIFICATIONS_TODAY',
    'NOTIFICATIONS_YESTERDAY',
    'NOTIFICATIONS_N_DAYS_AGO',
    'NOTIFICATIONS_ONE_WEEK_AGO',
    'NOTIFICATIONS_N_WEEKS_AGO',
    'NOTIFICATIONS_TYPES_CUSTOM_NAME',
    'NOTIFICATIONS_TYPES_CUSTOM_WELCOME_TO_NOTIFICATIONS',
    'NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_NAME',
    'NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_INFO',
    'NOTIFICATIONS_TYPES_DOCUMENT_RECEIVED_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_NAME',
    'NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_INFO',
    'NOTIFICATIONS_TYPES_BANK_IMPORT_COMPLETED_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_NAME',
    'NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_INFO',
    'NOTIFICATIONS_TYPES_NOTE_FOLLOW_UP_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_NAME',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_INFO',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_OVERDUE_GROUPED_INFO',
    'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_NAME',
    'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_INFO',
    'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_PURCHASE_INVOICE_OVERDUE_GROUPED_INFO',
    'NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_NAME',
    'NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_INFO',
    'NOTIFICATIONS_TYPES_REQUESTED_DOCUMENT_UPLOADED_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_NAME',
    'NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_INFO',
    'NOTIFICATIONS_TYPES_DOCUMENT_REQUEST_REJECTED_PLURAL_INFO',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_NAME',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_INFO',
    'NOTIFICATIONS_TYPES_SALES_INVOICE_SENT_ERROR_GROUPED_INFO',
    'NYETS_FORM_ACCOUNTS_EDITFIELD_MIRRORCODE',
    'SMARTPAY_MENU_HANGER_TEXT',
    'FORM_CREATE_ENTRY_NEW_SUPPLIER_MESSAGE',
    'DOCUMENT_INBOX_DOCUMENT_SETTINGS_SUGGESTIONS_TITLE',
    'DOCUMENT_INBOX_DOCUMENT_SUGGESTIONS_SETTING_DESCRIPTION',
    'DOCUMENT_INBOX_DOCUMENT_SUGGESTIONS_SETTING_INTRODUCTION',
    'FORM_PAYABLES_CREATE_HANGER',
    'FORM_CREATE_ENTRY_SUPPLIER_SUGGESTION_BANNER_TEXT',
    'FORM_CREATE_ENTRY_SUPPLIER_SUGGESTION_BANNER_BUTTON',
    'TOAST_DISTRIBUTION_TEMPLATE_UPDATE_SUCCESS',
    'TOAST_DISTRIBUTION_TEMPLATE_CREATE_SUCCESS',
    'FORM_PAYABLES_EDITTEXT_INLINE',
    'NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_NAME',
    'NOTIFICATIONS_TYPES_ELECTRONIC_DOCUMENT_RECEIVED',
    'NOTIFICATIONS_TYPES_DOCUMENTS_SENT_FOR_APPROVAL',
    'NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_INFO',
    'NOTIFICATIONS_TYPES_NEW_INVOICE_FROM_ECONOMIC_GROUPED_INFO',
    'ENTRYNUMBER_DROPDOWN_ASSIGN_MANUALLY',
    'ENTRYNUMBER_DROPDOWN_USE_SAME',
    'ENTRYNUMBER_DROPDOWN_USE_CONTINUOUS',
    'DIALOG_CHANGE_NUMBER',
    'FORM_ACCOUNTS_CHANGENO_HEADLINE',
    'FORM_ACCOUNTS_CHANGENO_CONFIRM',
    'FORM_ACCOUNTS_APPLETHEADLINE',
    'GIRO_ACCOUNT_VALIDATION_MESSAGE',
    'ACCOUNT_NUMBER_VALIDATION_MESSAGE',
    'FORM_FIELD_FI_SUPPLIER_NUMBER_VALIDATION_MESSAGE',
    'IBAN_OR_SWIFT_VALIDATION_MESSAGE',
    'SHARED_CHOICES_POSTTYPE_ALL',
];
